import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccessRestrictionStore } from '@/stores/access-restriction.store';
import { ROUTES } from '@/routes/routes';

/**
 * Routes to the access-restriction screen once the server has said this
 * workspace may not be used.
 *
 * Mounted in `AppLayout`, which is the parent of every protected tenant route —
 * so every page that can trigger a guarded request is covered by one mount. It
 * lives in a component rather than in the axios interceptor because navigation
 * belongs to the router, and importing the router into `api-client` would close
 * an import cycle (router → pages → services → api-client).
 *
 * ── Why this cannot loop ────────────────────────────────────────────────────
 * The target route sits outside `AppLayout`, so this component unmounts the
 * moment it navigates. `AccessRestrictedPage` issues no API calls, so nothing
 * there can set the flag again. Coming back to a tenant URL remounts this and
 * bounces once more — one hop, which is the intended "cannot navigate back in"
 * behaviour, not a cycle. Signing out clears the flag, so the login page is
 * reachable and a fresh sign-in starts from a clean state.
 */
export function AccessRestrictionRedirect() {
  const reason = useAccessRestrictionStore((s) => s.reason);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (reason === null) return;
    if (location.pathname === ROUTES.ACCESS_RESTRICTED) return;
    // `reason` is carried in the URL for presentation only, so a refresh of the
    // restricted page still renders the right copy after the in-memory store is
    // gone. It is re-validated on read and grants nothing.
    navigate(`${ROUTES.ACCESS_RESTRICTED}?reason=${reason}`, { replace: true });
  }, [reason, location.pathname, navigate]);

  return null;
}
